import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

export const makeStringSearchParamHook = (key: string) => <DefaultType extends string | undefined>(
  defaultValue: DefaultType
): [DefaultType extends string ? string : string | null , (newValue: string) => void] => {
  const [searchParams, setSearchParams] = useSearchParams()
  const searchParam = (searchParams.get(key) ?? defaultValue ?? null)
  const handleUpdate = useCallback((newValue: string) => {
    setSearchParams((prev) => {
      prev.set(key, newValue)
      return prev
    })
  }, [setSearchParams])

  // Need to coerce as non-null here since typescript can't follow the conditional type
  return [searchParam!, handleUpdate]
}

export const makeEnumSearchParamHook = <Enumeration extends string[]>(key: string, enumeration: Enumeration) => {
  const useStringSearchParam = makeStringSearchParamHook(key)
  const enumSet = new Set(enumeration)
  return (
    defaultValue: Enumeration[number]
  ): [Enumeration[number], (newValue: string) => void] => {
    const [searchParam, handleUpdate] = useStringSearchParam(defaultValue)
    const handleUpdateEnum = useCallback((newValue: string) => {
      const newEnumValue = enumSet.has(newValue) ? newValue : null
      if (newEnumValue) {
        handleUpdate(newEnumValue)
      }
    }, [enumeration, handleUpdate])
    return [searchParam as Enumeration[number], handleUpdateEnum]
  }
}

export const makeBooleanSearchParamHook = (key: string) => {
  const useStringSearchParam = makeStringSearchParamHook(key)
  return (
    defaultValue = false
  ): [boolean, (newValue: string) => void] => {
    const [searchParam, handleUpdate] = useStringSearchParam(defaultValue ? 'true' : 'false')
    const handleUpdateBoolean = useCallback((newValue: string) => {
      handleUpdate(newValue === 'true' ? 'true' : 'false')
    }, [handleUpdate])
    return [searchParam === 'true', handleUpdateBoolean]
  }
}
